import { useEffect, useRef } from 'react';
import { Events } from '../firebase/telemetry';

/**
 * Answers "which overlays, base maps and imagery products do people actually
 * use?" by observing the visibility state objects rather than instrumenting
 * every setter. One hook call covers the sidebar, the 40E wizard, the imagery
 * controls and preset application alike — including any control added later,
 * which is the point: a new checkbox is tracked the day it is written.
 *
 * Three complementary signals, because toggles alone are misleading:
 *
 *   layer_toggled      — an intentional act (someone turned this ON)
 *   overlay_state      — what a session actually RAN WITH. A user who loads the
 *                        default preset and never touches it emits zero
 *                        toggles, but still used the hybrid base map all day.
 *   overlay_engagement — how LONG each layer was on screen, which separates a
 *                        layer people leave on from one they flick on and off.
 */

// Mutually exclusive base layers, in priority order (the first visible one
// wins, matching how the map renders them).
const BASE_MAP_KEYS = ['sentinel2Dated', 'sentinel2', 'hybrid', 'topo', 'geo100', 'geo200', 'bedrock', 'magnetic', 'street'];

// How long the map must sit unchanged before the state snapshot is taken, so
// a burst of toggles produces one snapshot rather than one per checkbox.
const SETTLE_MS = 5000;

// Layers that were on for less than this are noise — a mis-click, or a preset
// being applied over the top of another.
const MIN_ENGAGEMENT_MS = 3000;

// Sliders and text inputs fire continuously; report the settled value.
const FILTER_DEBOUNCE_MS = 1500;

/** GA4 caps string parameter values at 100 chars. */
const clip = (s, max = 100) => (s.length > max ? `${s.slice(0, max - 1)}~` : s);

/** asterFerricOxideContent -> FerricOxideContent, so more ids fit in 100 chars. */
const shorten = (key, prefix) =>
  key.startsWith(prefix) ? key.slice(prefix.length) : key;

const activeKeys = (obj) =>
  Object.keys(obj || {}).filter(k => obj[k] === true);

function activeBaseMap(layerVisibility) {
  return BASE_MAP_KEYS.find(k => layerVisibility?.[k]) || 'none';
}

export function useOverlayTelemetry({
  layerVisibility,
  overlayVisibility,
  asterVisibility,
  deaVisibility,
  terrain,
  zoom,
  // Non-boolean settings. These never appear in a toggle diff, but they are
  // part of what a session ran with: an ASTER layer at 10% opacity is a very
  // different thing from the same layer at 100%.
  asterOpacity,
  deaOpacity,
  deaOptions,
  imageryPrefs,
  // Set to true by the caller immediately before applying a preset, so the
  // resulting toggle burst is labelled 'preset' instead of masquerading as a
  // deliberate user action. Labelling beats suppressing: the toggles are still
  // real usage, they just have a different cause.
  changeSourceRef,
}) {
  const prevRef = useRef(null);
  const settleTimerRef = useRef(null);
  // layer -> { since: epoch ms | null, total: ms, group }
  const engagementRef = useRef(new Map());
  const flushedRef = useRef(false);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const settingsRef = useRef({});
  settingsRef.current = { asterOpacity, deaOpacity, deaOptions, imageryPrefs };

  // --- engagement bookkeeping ------------------------------------------
  const markVisible = (layer, group, on, now) => {
    const map = engagementRef.current;
    let e = map.get(layer);
    if (!e) {
      e = { since: null, total: 0, group };
      map.set(layer, e);
    }
    if (on && e.since === null) {
      e.since = now;
    } else if (!on && e.since !== null) {
      e.total += now - e.since;
      e.since = null;
    }
  };

  const flushEngagement = () => {
    // Only once per page life — visibilitychange can fire repeatedly (tab
    // switching), and re-reporting would multiply the totals.
    if (flushedRef.current) return;
    flushedRef.current = true;

    const now = Date.now();
    engagementRef.current.forEach((e, layer) => {
      const total = e.total + (e.since !== null ? now - e.since : 0);
      if (total >= MIN_ENGAGEMENT_MS) {
        Events.overlayEngagement(layer, Math.round(total / 1000), e.group);
      }
    });
  };

  // --- diff + snapshot --------------------------------------------------
  useEffect(() => {
    const now = Date.now();
    const groups = [
      ['base', layerVisibility],
      ['overlay', overlayVisibility],
      ['aster', asterVisibility],
      ['dea', deaVisibility],
      ['terrain', terrain],
    ];

    const prev = prevRef.current;
    const source = changeSourceRef?.current ? 'preset' : 'user';
    if (changeSourceRef) changeSourceRef.current = false;

    groups.forEach(([group, current]) => {
      Object.keys(current || {}).forEach(key => {
        const isOn = current[key] === true;
        const wasOn = prev ? prev[group]?.[key] === true : null;

        // First pass establishes the baseline: record what is already on for
        // engagement, but do not report it as a toggle — nobody toggled it.
        if (prev && wasOn !== isOn) {
          Events.layerToggled(key, isOn, group, source);
        }
        if (typeof current[key] === 'boolean') {
          markVisible(key, group, isOn, now);
        }
      });
    });

    // Base map switches arrive as two toggles (old off, new on). Report the
    // transition as one event so "what did they switch away from" is answerable.
    const base = activeBaseMap(layerVisibility);
    const prevBase = prev ? activeBaseMap(prev.base) : null;
    if (prev && base !== prevBase) {
      Events.baseMapChanged(base, prevBase);
    }

    prevRef.current = {
      base: { ...(layerVisibility || {}) },
      overlay: { ...(overlayVisibility || {}) },
      aster: { ...(asterVisibility || {}) },
      dea: { ...(deaVisibility || {}) },
      terrain: { ...(terrain || {}) },
    };

    // Snapshot after things settle.
    clearTimeout(settleTimerRef.current);
    settleTimerRef.current = setTimeout(() => {
      const overlays = activeKeys(overlayVisibility);
      const aster = activeKeys(asterVisibility).map(k => shorten(k, 'aster'));
      const dea = activeKeys(deaVisibility).map(k => shorten(k, 'dea'));

      const settings = settingsRef.current;
      Events.overlayState({
        baseMap: base,
        overlays: clip(overlays.join('|')) || 'none',
        overlayCount: overlays.length,
        aster: clip(aster.join('|')) || 'none',
        asterCount: aster.length,
        dea: clip(dea.join('|')) || 'none',
        deaCount: dea.length,
        hillshade: !!terrain?.hillshade,
        zoom: zoomRef.current,
        asterOpacity: settings.asterOpacity,
        deaOpacity: settings.deaOpacity,
        geomadYear: settings.deaOptions?.geomadYear,
        geomadStyle: settings.deaOptions?.geomadStyle,
        imagerySensor: settings.imageryPrefs?.sensor,
        imageryMode: settings.imageryPrefs?.mode,
        imageryRange: settings.imageryPrefs?.rangeId,
        imageryStyle: settings.imageryPrefs?.styleId,
      });
    }, SETTLE_MS);

    return () => clearTimeout(settleTimerRef.current);
  }, [layerVisibility, overlayVisibility, asterVisibility, deaVisibility, terrain, changeSourceRef]);

  // --- deliberate changes to non-boolean controls -----------------------
  // Debounced: an opacity slider emits a value per pixel dragged, and the
  // number people settle on is the only one worth having.
  const optsPrevRef = useRef(null);
  const optsTimerRef = useRef(null);
  useEffect(() => {
    const next = {
      asterOpacity,
      deaOpacity,
      geomadYear: deaOptions?.geomadYear,
      geomadStyle: deaOptions?.geomadStyle,
      sensor: imageryPrefs?.sensor,
      mode: imageryPrefs?.mode,
      rangeId: imageryPrefs?.rangeId,
      styleId: imageryPrefs?.styleId,
      scanSensor: imageryPrefs?.scanSensor,
    };
    const prev = optsPrevRef.current;
    if (!prev) {
      // Mount is the stored/default configuration, not a choice someone made.
      optsPrevRef.current = next;
      return;
    }

    clearTimeout(optsTimerRef.current);
    optsTimerRef.current = setTimeout(() => {
      const settled = optsPrevRef.current;
      if (next.asterOpacity !== settled.asterOpacity) Events.overlayOpacityChanged('aster', next.asterOpacity);
      if (next.deaOpacity !== settled.deaOpacity) Events.overlayOpacityChanged('dea', next.deaOpacity);
      // Which imagery MODE someone chose is the question the time-series work
      // needs answered, and none of it is a checkbox.
      [['geomad_year', 'geomadYear'], ['geomad_style', 'geomadStyle'],
       ['sensor', 'sensor'], ['mode', 'mode'], ['range', 'rangeId'],
       ['style', 'styleId'], ['scan_sensor', 'scanSensor']].forEach(([label, key]) => {
        if (next[key] !== settled[key]) Events.imageryOptionChanged(label, next[key]);
      });
      optsPrevRef.current = next;
    }, FILTER_DEBOUNCE_MS);

    return () => clearTimeout(optsTimerRef.current);
  }, [asterOpacity, deaOpacity, deaOptions, imageryPrefs]);

  // --- flush engagement when the session ends ---------------------------
  useEffect(() => {
    // 'hidden' is the only reliable end-of-session signal on mobile — pagehide
    // and unload are not guaranteed to fire when an app is backgrounded.
    const onHidden = () => {
      if (document.visibilityState === 'hidden') flushEngagement();
    };
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', flushEngagement);
    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', flushEngagement);
    };
  }, []);
}

/**
 * Which filters people narrow on, and how hard.
 *
 * Observed rather than wrapped around the setters, because filters change on
 * every keystroke and slider tick; a debounce here reports the value people
 * settled on instead of one event per character typed.
 *
 * Only bucketed values and counts leave the browser. Never a holder name, an
 * ANumber, or an exact grade threshold — those describe what a specific user is
 * hunting for, which is their business, not ours.
 */

export function useFilterTelemetry({ assayFilters, drillHoleFilters, tenementFilters, minedexFilters }) {
  const timerRef = useRef(null);
  const firstRef = useRef(true);
  const lastRef = useRef({});

  useEffect(() => {
    // The initial mount is the default preset, not a filter someone applied.
    if (firstRef.current) {
      firstRef.current = false;
      lastRef.current = { assayFilters, drillHoleFilters, tenementFilters, minedexFilters };
      return;
    }

    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const prev = lastRef.current;
      const len = v => (Array.isArray(v) ? v.length : 0);

      if (assayFilters !== prev.assayFilters) {
        Events.filtersApplied('assays', {
          au_ppb_min: Number(assayFilters?.ppbmt) || 0,
          au_ppb_max: Number(assayFilters?.ppblt) || 0,
          sample_type_count: len(assayFilters?.type),
          include_nulls: assayFilters?.nullValues ? 'on' : 'off',
          date_filter: assayFilters?.dateFilterEnabled ? 'on' : 'off',
        });
      }
      if (drillHoleFilters !== prev.drillHoleFilters) {
        Events.filtersApplied('drillholes', {
          au_gpt_min: Number(drillHoleFilters?.gptmt) || 0,
          au_gpt_max: Number(drillHoleFilters?.gptlt) || 0,
          max_depth: Number(drillHoleFilters?.maxDepth) || 0,
        });
      }
      if (tenementFilters !== prev.tenementFilters) {
        Events.filtersApplied('tenements', {
          include_live: tenementFilters?.includeLive ? 'on' : 'off',
          include_pending: tenementFilters?.includePending ? 'on' : 'off',
          include_dead: tenementFilters?.includeDead ? 'on' : 'off',
          type_count: len(tenementFilters?.types),
          granted_only: tenementFilters?.grantedOnly ? 'on' : 'off',
          expiring_soon: tenementFilters?.expiringSoonEnabled ? 'on' : 'off',
          // Whether they searched by holder, never WHO they searched for.
          holder_search: tenementFilters?.holderNameLike ? 'on' : 'off',
        });
      }
      if (minedexFilters !== prev.minedexFilters) {
        Events.filtersApplied('minedex', {
          commodity_count: len(minedexFilters?.commodityCategory),
          target_count: len(minedexFilters?.targetCommodity),
          site_type_count: len(minedexFilters?.siteType),
        });
      }

      lastRef.current = { assayFilters, drillHoleFilters, tenementFilters, minedexFilters };
    }, FILTER_DEBOUNCE_MS);

    return () => clearTimeout(timerRef.current);
  }, [assayFilters, drillHoleFilters, tenementFilters, minedexFilters]);
}
